/**
 * What the ground gives away (01 §5): a marked minefield shows as "tilled, wavy ground"
 * with warning stakes at its corners, and a single sensed mine as a small dark disc.
 * Furrows are built in world space because each one sits on the terrain under it.
 */

import type { Pcg32 } from '../../sim/rng';
import type { Rect } from '../../sim/types';
import type { Palette } from '../palette';
import type { KitBuilder } from './flora';
import { MeshBuilder, place } from './MeshBuilder';

const ROW_SPACING = 1.0;
const FURROW_LENGTH = 1.8;
const WAVE = 0.22;
const WAVE_FREQ = 0.9;

export interface MineFieldTell {
  /** Ground detail: draw WITHOUT an outline, like the terrain it lies on. */
  furrows: MeshBuilder;
  /** Corner stakes: ordinary outlined props. */
  stakes: MeshBuilder;
}

/**
 * Rows of low dark ridges running east-west across the field's rectangle, snaking a little
 * so they cannot be mistaken for a road's edge or a building's shadow.
 */
export function mineFieldTell(
  p: Palette,
  rng: Pcg32,
  rect: Rect,
  groundY: (x: number, z: number) => number,
): MineFieldTell {
  const furrows = new MeshBuilder();
  // Turned earth; in the arctic, furrowed snow in the ramp's shadow tone (dark earth would shout).
  const ridge = p.color(p.biome === 'arctic' ? 'ground.0' : 'dirt.lo');
  const crest = p.color(p.biome === 'arctic' ? 'ground.1' : 'dirt.mid');
  let row = 0;
  for (let z = rect.z0 + ROW_SPACING / 2; z < rect.z1; z += ROW_SPACING, row++) {
    const phase = row * 1.3 + rng.range(0, 0.6);
    const count = Math.max(1, Math.floor((rect.x1 - rect.x0) / FURROW_LENGTH));
    const pitch = (rect.x1 - rect.x0) / count;
    for (let i = 0; i < count; i++) {
      const x = rect.x0 + pitch * (i + 0.5);
      const wz = z + Math.sin(x * WAVE_FREQ + phase) * WAVE;
      // Turn each ridge along the wave, so the row reads as one wavy line rather than dashes.
      const yaw = -Math.atan(Math.cos(x * WAVE_FREQ + phase) * WAVE * WAVE_FREQ);
      furrows.gable(pitch - 0.25, 0.15, 0.5, i % 2 ? ridge : crest, place(x, groundY(x, wz) - 0.02, wz, yaw), ridge);
    }
  }

  const stakes = new MeshBuilder();
  for (const x of [rect.x0, rect.x1]) {
    for (const z of [rect.z0, rect.z1]) {
      stakes.with(place(x, groundY(x, z) - 0.05, z, rng.range(-0.15, 0.15)), () => {
        stakes.box(0.1, 0.95, 0.1, p.color('timber.lo'));
        stakes.box(0.36, 0.28, 0.06, p.color('enemyAccent.0'), place(0, 0.62, 0.06));
        stakes.box(0.16, 0.1, 0.08, p.color('paper.0'), place(0, 0.71, 0.07));
      });
    }
  }
  return { furrows, stakes };
}

/** The X on the map, painted on the ground at the pickup: two crossed strips in the player's colour. */
export const pickupMark: KitBuilder = (p) => {
  const mb = new MeshBuilder();
  for (const yaw of [Math.PI / 4, -Math.PI / 4]) mb.box(3.2, 0.04, 0.5, p.color('playerAccent.0'), place(0, 0, 0, yaw));
  return mb;
};

/** One sensed mine: a dark pressure plate barely proud of the ground. */
export const mineDisc: KitBuilder = (p) => {
  const mb = new MeshBuilder();
  mb.cylinder(0.24, 0.2, 0.06, 8, p.color('ink.1'), undefined, p.color('ink.0'));
  mb.box(0.1, 0.05, 0.1, p.color('metal.lo'), place(0, 0.06, 0));
  return mb;
};
